import { z } from "zod";

/**
 * Materialen (docs/MATERIALEN.md, issue #173): artikellijst per klant of gebouw, reservevoorraad in het
 * gebouw, materialen per onderhoudsbeurt en per taak, afboeken bij afronden.
 */
export const MATERIAL_UNITS = ["stuk", "set", "doos", "rol", "meter", "liter", "kg"] as const;
export type MaterialUnit = (typeof MATERIAL_UNITS)[number];

/** Geldt voor alle gebouwen van de klant, of alleen voor dit gebouw. */
export const MATERIAL_SCOPES = ["client", "building"] as const;
export type MaterialScope = (typeof MATERIAL_SCOPES)[number];
export const MATERIAL_SCOPE_LABELS: Record<MaterialScope, string> = {
  client: "Alle gebouwen van de klant",
  building: "Alleen dit gebouw",
};

/** Per regel op een taak: uit de voorraad in het gebouw, of bestellen. */
export const MATERIAL_SOURCES = ["stock", "order"] as const;
export type MaterialSource = (typeof MATERIAL_SOURCES)[number];
export const MATERIAL_SOURCE_LABELS: Record<MaterialSource, string> = { stock: "Uit voorraad", order: "Bestellen" };

export const MATERIAL_ORDER_STATUSES = ["to_order", "ordered", "delivered"] as const;
export type MaterialOrderStatus = (typeof MATERIAL_ORDER_STATUSES)[number];
export const MATERIAL_ORDER_STATUS_LABELS: Record<MaterialOrderStatus, string> = {
  to_order: "Te bestellen",
  ordered: "Besteld",
  delivered: "Geleverd",
};

export const STOCK_MOVE_KINDS = ["received", "used", "correction"] as const;
export type StockMoveKind = (typeof STOCK_MOVE_KINDS)[number];
export const STOCK_MOVE_KIND_LABELS: Record<StockMoveKind, string> = {
  received: "Ontvangen",
  used: "Afgeboekt",
  correction: "Correctie",
};

/** Aantal met hooguit drie decimalen (meter, liter), groter dan of gelijk aan 0. */
const qty = z.number().min(0).max(1_000_000);
const posQty = z.number().gt(0).max(1_000_000);
const optText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v ? v : null));

export const materialCreateSchema = z.object({
  id: z.uuid(),
  scope: z.enum(MATERIAL_SCOPES),
  name: z.string().trim().min(1, "Vul een naam in").max(120),
  articleNo: optText(60),
  unit: z.enum(MATERIAL_UNITS).default("stuk"),
  priceCents: z.number().int().min(0).max(100_000_000).nullish(),
  supplierCompanyId: z.uuid().nullish(),
  notes: optText(500),
});
export type MaterialCreate = z.infer<typeof materialCreateSchema>;

export const materialUpdateSchema = z
  .object({
    name: z.string().trim().min(1, "Vul een naam in").max(120),
    articleNo: optText(60),
    unit: z.enum(MATERIAL_UNITS),
    priceCents: z.number().int().min(0).max(100_000_000).nullable(),
    supplierCompanyId: z.uuid().nullable(),
    notes: optText(500),
    archived: z.boolean(),
  })
  .partial();

/** Minimum en plek van de voorraad in een gebouw. */
export const stockUpdateSchema = z
  .object({
    minQuantity: qty.nullable(),
    location: optText(120),
  })
  .partial();

/** Ontvangen: aantal erbij. Correctie: het getelde aantal (nieuwe stand). */
export const stockMoveSchema = z.object({
  kind: z.enum(["received", "correction"]),
  quantity: qty,
  note: optText(300),
});

export const obligationMaterialsSchema = z.object({
  lines: z.array(z.object({ materialId: z.uuid(), quantity: posQty })).max(50),
});

export const taskMaterialsSchema = z.object({
  lines: z
    .array(
      z.object({
        id: z.uuid(),
        materialId: z.uuid(),
        quantity: posQty,
        source: z.enum(MATERIAL_SOURCES),
      }),
    )
    .max(100),
});

export const taskMaterialOrderSchema = z.object({ orderStatus: z.enum(MATERIAL_ORDER_STATUSES) });

/** Overzicht Te bestellen: meerdere regels tegelijk op Besteld of Geleverd zetten. */
export const taskMaterialsOrderBulkSchema = z.object({
  ids: z.array(z.uuid()).min(1).max(200),
  orderStatus: z.enum(MATERIAL_ORDER_STATUSES),
});

/** Afboeken bij afronden: werkelijk gebruikt aantal per regel (0 = niets gebruikt). */
export const taskMaterialsBookSchema = z.object({
  lines: z
    .array(z.object({ id: z.uuid(), usedQuantity: qty }))
    .min(1)
    .max(100),
});

export interface MaterialStockDto {
  quantity: number;
  minQuantity: number | null;
  location: string | null;
}

export interface MaterialDto {
  id: string;
  scope: MaterialScope;
  name: string;
  articleNo: string | null;
  unit: MaterialUnit;
  /** Prijs per eenheid excl. btw. */
  priceCents: number | null;
  supplier: { id: string; name: string } | null;
  notes: string | null;
  archived: boolean;
  /** Voorraad in het gebouw waarvoor de lijst is opgevraagd; null = nog nooit iets vastgelegd. */
  stock: MaterialStockDto | null;
  /** Onderhoudsbeurten in dit gebouw die het artikel gebruiken. */
  /** `assetId` null = onderhoud bij het gebouw zelf (`assetCode` is dan leeg). */
  usedBy: {
    obligationId: string;
    obligationName: string;
    assetId: string | null;
    assetCode: string | null;
    quantity: number;
  }[];
}

export interface StockMoveDto {
  id: string;
  kind: StockMoveKind;
  /** Verandering van de stand (+ of −). */
  delta: number;
  /** Stand na de mutatie. */
  balance: number;
  note: string | null;
  task: { id: string; code: string } | null;
  by: string | null;
  createdAt: string;
}

export interface ObligationMaterialDto {
  materialId: string;
  name: string;
  articleNo: string | null;
  unit: MaterialUnit;
  quantity: number;
  priceCents: number | null;
  /** Aantal × prijs; null zonder prijs. */
  totalCents: number | null;
}

export interface ObligationMaterialsResponse {
  lines: ObligationMaterialDto[];
  /** Som van de regels met een prijs: telt in de MJOP op bij *per keer*. */
  totalCents: number;
}

export interface TaskMaterialDto {
  id: string;
  materialId: string;
  name: string;
  articleNo: string | null;
  unit: MaterialUnit;
  quantity: number;
  source: MaterialSource;
  /** Alleen bij *Bestellen*. */
  orderStatus: MaterialOrderStatus | null;
  /** Na afboeken: werkelijk gebruikt. */
  usedQuantity: number | null;
  bookedAt: string | null;
  /** Huidige voorraad in het gebouw en de plek. */
  stockQuantity: number | null;
  location: string | null;
}

/** Bedrag van een regel in hele centen. */
export const lineCents = (quantity: number, priceCents: number | null) =>
  priceCents == null ? null : Math.round(quantity * priceCents);

/** Aantal zoals de app het toont: 2, 1,5 of 0,25. */
export const formatQty = (n: number) => n.toLocaleString("nl-NL", { maximumFractionDigits: 3 });

/** Onder het minimum (alleen als er een minimum is). */
export const belowMinimum = (s: MaterialStockDto | null) =>
  s != null && s.minQuantity != null && s.quantity < s.minQuantity;

type Ref = { id: string; name: string };
type BuildingRef = { id: string; code: string; name: string };

/** Overzicht *Te bestellen*: regel *Bestellen* op een taak die nog niet geleverd is. */
export interface OrderLineDto {
  id: string;
  quantity: number;
  orderStatus: MaterialOrderStatus;
  building: BuildingRef;
  client: Ref | null;
  task: { id: string; code: string; title: string; dueDate: string | null };
  material: { id: string; name: string; articleNo: string | null; unit: MaterialUnit; priceCents: number | null };
  supplier: Ref | null;
}

/** Overzicht *Te bestellen*: artikel waarvan de voorraad in een gebouw onder het minimum ligt. */
export interface LowStockDto {
  building: BuildingRef;
  client: Ref | null;
  material: { id: string; name: string; articleNo: string | null; unit: MaterialUnit; priceCents: number | null };
  supplier: Ref | null;
  quantity: number;
  minQuantity: number;
  location: string | null;
  /** Al besteld of te bestellen voor taken in dit gebouw (niet geleverd). */
  onOrder: number;
}

export interface MaterialOrdersResponse {
  lines: OrderLineDto[];
  lowStock: LowStockDto[];
}
